<template>
  <div class="modal-mask" @click.self="fechar">
    <div class="modal-conteudo">
      <h3>Nova OS</h3>

      <div class="formulario">
        <div class="campo">
          <label>Pós-venda *</label>
          <select
            v-model.number="form.pos_venda_id"
            required
            :disabled="carregandoPosVendas || !posVendas.length || posVendaTravado"
          >
            <option :value="null">Selecione...</option>
            <option v-for="pv in posVendas" :key="pv.id" :value="pv.id">
              {{ labelPosVenda(pv) }}
            </option>
          </select>
        </div>
        <div class="campo">
          <label>Cliente</label>
          <p class="data-calculada">{{ clienteLabel }}</p>
        </div>

        <div class="campo">
          <label>NF de remessa do cliente <span class="obrigatorio">*</span></label>
          <input
            v-model="form.nf_entrada"
            type="text"
            placeholder="Número da NF de remessa do cliente"
            autocomplete="off"
            required
          />
        </div>

        <div class="campos-datas">
          <div class="campo">
            <label>Data prevista de recebimento do material *</label>
            <input v-model="form.dt_prevista_recebimento_material" type="date" required />
          </div>
          <div class="campo">
            <label>Data prevista de encerramento da OS</label>
            <p class="data-calculada">{{ formatarDataBr(dtFimPrevistoExibicao) }}</p>
          </div>
        </div>

        <div class="campo">
          <label>Observações</label>
          <textarea
            v-model="form.observacoes"
            rows="3"
            placeholder="Descreva o problema relatado pelo cliente…"
          />
        </div>

        <div class="campo">
          <label>Produtos <span class="obrigatorio">*</span></label>
          <p class="hint-produtos">
            Informe ao menos um produto com a quantidade descrita pelo cliente. A quantidade
            recebida será conferida depois, no status «Em conferência».
          </p>
          <table v-if="form.produtos.length" class="tabela-produtos">
            <colgroup>
              <col />
              <col class="col-qtd" />
              <col class="col-acoes" />
            </colgroup>
            <thead>
              <tr>
                <th class="th-produto">Produto</th>
                <th class="th-qtd">Qtd. descrita <span class="obrigatorio">*</span></th>
                <th class="th-acoes"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in form.produtos" :key="row._key">
                <td class="td-produto">
                  <template v-if="!produtoLinhaRegistrada(row)">
                    <div class="busca-prod">
                      <input
                        v-model="row._busca"
                        type="text"
                        placeholder="Código ou descrição (min. 2 caracteres)"
                        autocomplete="off"
                        @input="onInputBuscaProduto(idx, $event.target.value)"
                        @focus="row._dropdown = true"
                        @blur="fecharDropdownDelayed(idx)"
                      />
                      <ul
                        v-if="row._dropdown && (row._buscando || row._resultados.length)"
                        class="dropdown-prod"
                      >
                        <li v-if="row._buscando">Buscando…</li>
                        <template v-else>
                          <li
                            v-for="p in row._resultados"
                            :key="p.produto_cod"
                            class="opcao"
                            @mousedown.prevent="selecionarProduto(idx, p)"
                          >
                            {{ p.cod || p.produto_cod }} — {{ p.desc || "" }}
                          </li>
                        </template>
                      </ul>
                    </div>
                  </template>
                  <p v-else class="prod-confirmado-texto" :title="resumoProduto(row)">
                    {{ resumoProduto(row) }}
                  </p>
                </td>
                <td>
                  <input
                    v-if="produtoLinhaRegistrada(row)"
                    :value="row.qt_descrita ?? ''"
                    type="text"
                    inputmode="decimal"
                    class="input-curto"
                    autocomplete="off"
                    required
                    placeholder="0"
                    @input="onInputQuantidadeBr($event, row, 'qt_descrita')"
                  />
                  <span v-else class="texto-na">—</span>
                </td>
                <td class="td-acoes">
                  <div class="btn-remover" @click="removerLinha(idx)">Remover</div>
                </td>
              </tr>
            </tbody>
          </table>
          <button type="button" class="acao-secundaria btn-add-linha" @click="adicionarLinha">
            Adicionar produto
          </button>
        </div>
      </div>

      <div class="acoes">
        <button type="button" class="acao-secundaria" @click="fechar">Cancelar</button>
        <button type="button" class="acao-primaria" :disabled="salvando" @click="salvar">
          {{ salvando ? "Salvando…" : "Salvar" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  buscarProdutosOs2,
  calcularDtFimPrevisto7DiasUteis,
  criarAssistenciaEmAnalise,
  listarClientesOsAssistencia,
  listarPosVendasOsAssistencia,
} from "../services/os2AssistenciaOrdemServicoService";

function sanitizarQuantidadeBr(raw) {
  let t = String(raw ?? "").replace(/[^\d,]/g, "");
  if (t.startsWith(",")) t = `0${t}`;
  const i = t.indexOf(",");
  if (i === -1) return t;
  const intPart = t.slice(0, i).replace(/\D/g, "");
  const fracPart = t.slice(i + 1).replace(/\D/g, "");
  if (fracPart.length) return `${intPart},${fracPart}`;
  return intPart === "" ? "," : `${intPart},`;
}

function parseQuantidadeBrParaNumero(raw) {
  let s = sanitizarQuantidadeBr(String(raw ?? "").trim());
  if (s === "" || s === ",") return null;
  if (s.endsWith(",")) s = s.slice(0, -1);
  if (s === "") return null;
  const n = Number(s.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

let _linhaKey = 0;
function novaLinhaKey() {
  _linhaKey += 1;
  return `linha-${_linhaKey}`;
}

function linhaProdutoVazia() {
  return {
    _key: novaLinhaKey(),
    produto_cod: null,
    qt_descrita: null,
    _busca: "",
    _resultados: [],
    _buscando: false,
    _dropdown: false,
    _produto: null,
  };
}

export default {
  name: "ModalNovaOSAssistenciaTecnica",
  emits: ["fechar", "salvo", "notificar"],
  props: {
    posVendaIdPreSelecionado: {
      type: [Number, String],
      default: null,
    },
    clienteIdPreSelecionado: {
      type: [Number, String],
      default: null,
    },
    observacoesIniciais: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      clientes: [],
      posVendas: [],
      carregandoClientes: true,
      carregandoPosVendas: true,
      salvando: false,
      form: {
        pos_venda_id: null,
        cliente_id: null,
        dt_prevista_recebimento_material: "",
        observacoes: "",
        nf_entrada: "",
        produtos: [],
      },
      produtoBuscaTimers: {},
    };
  },
  computed: {
    posVendaTravado() {
      return (
        this.posVendaIdPreSelecionado != null &&
        this.posVendaIdPreSelecionado !== ""
      );
    },
    clienteLabel() {
      if (this.form.cliente_id == null) {
        return this.form.pos_venda_id != null
          ? "Cliente não vinculado ao pós-venda"
          : "Selecione o pós-venda";
      }
      const pv = this.posVendas.find(
        (p) => Number(p.id) === Number(this.form.pos_venda_id),
      );
      const cliPv = pv?.cliente;
      if (cliPv) {
        const nome = cliPv.razao_social ?? cliPv.nome ?? "";
        const doc = cliPv.CNPJ ? String(cliPv.CNPJ).trim() : "";
        return doc ? `${nome} (${doc})` : nome || "—";
      }
      const c = this.clientes.find(
        (x) => Number(x.id) === Number(this.form.cliente_id),
      );
      return c ? this.labelCliente(c) : "—";
    },
    dtFimPrevistoExibicao() {
      return calcularDtFimPrevisto7DiasUteis(this.form.dt_prevista_recebimento_material);
    },
  },
  watch: {
    "form.pos_venda_id"(id) {
      this.aplicarClienteDoPosVenda(id);
    },
  },
  async mounted() {
    try {
      const [clientesRaw, posVendasRaw] = await Promise.all([
        listarClientesOsAssistencia(),
        listarPosVendasOsAssistencia(),
      ]);
      this.clientes = (Array.isArray(clientesRaw) ? clientesRaw : []).map((c) => ({
        ...c,
        nomeCompleto: c.nome ?? c.nomeCompleto,
      }));
      this.posVendas = Array.isArray(posVendasRaw) ? posVendasRaw : [];
    } catch {
      this.clientes = [];
      this.posVendas = [];
      this.notificar("error", "Não foi possível carregar dados do formulário.");
    } finally {
      this.carregandoClientes = false;
      this.carregandoPosVendas = false;
    }

    const pre = this.posVendaIdPreSelecionado;
    if (pre != null && pre !== "") {
      const id = Number(pre);
      if (Number.isFinite(id)) {
        this.form.pos_venda_id = id;
        this.aplicarClienteDoPosVenda(id);
      }
    }
    if (this.form.cliente_id == null && this.clienteIdPreSelecionado != null) {
      const cid = Number(this.clienteIdPreSelecionado);
      if (Number.isFinite(cid)) {
        this.form.cliente_id = cid;
      }
    }
    const fato = String(this.observacoesIniciais ?? "").trim();
    if (fato) {
      this.form.observacoes = fato;
    }
    if (!this.form.produtos.length) {
      this.adicionarLinha();
    }
  },
  beforeUnmount() {
    Object.keys(this.produtoBuscaTimers).forEach((k) =>
      clearTimeout(this.produtoBuscaTimers[k]),
    );
  },
  methods: {
    notificar(tipo, mensagem) {
      this.$emit("notificar", { tipo, mensagem });
    },
    formatarDataBr(dataIso) {
      const iso =
        dataIso != null && String(dataIso).trim() !== ""
          ? String(dataIso).trim().slice(0, 10)
          : "";
      if (!iso) return "—";
      const [ano, mes, dia] = iso.split("-");
      if (!ano || !mes || !dia) return "—";
      return `${dia}/${mes}/${ano}`;
    },
    labelCliente(c) {
      if (!c) return "";
      const n = c.nome ?? c.nomeCompleto ?? "";
      const doc = c.CNPJ ? String(c.CNPJ).trim() : "";
      return doc ? `${n} (${doc})` : n;
    },
    labelPosVenda(pv) {
      if (!pv) return "";
      const cod = pv.codigo ?? `ID ${pv.id}`;
      const cli = pv.cliente?.razao_social ?? pv.cliente?.nome ?? "";
      return cli ? `${cod} — ${cli}` : cod;
    },
    aplicarClienteDoPosVenda(posVendaId) {
      if (posVendaId == null) return;
      const pv = this.posVendas.find((p) => Number(p.id) === Number(posVendaId));
      const cid = pv?.cliente_id ?? pv?.cliente?.id;
      if (cid != null) {
        this.form.cliente_id = Number(cid);
      }
    },
    fechar() {
      this.$emit("fechar");
    },
    adicionarLinha() {
      this.form.produtos.push(linhaProdutoVazia());
    },
    produtoLinhaRegistrada(row) {
      return row?.produto_cod != null && !Number.isNaN(Number(row.produto_cod));
    },
    qtDescritaValida(row) {
      const n = parseQuantidadeBrParaNumero(row?.qt_descrita);
      return n != null && n > 0;
    },
    removerLinha(idx) {
      if (!this.form.produtos[idx]) return;
      this.form.produtos.splice(idx, 1);
      if (!this.form.produtos.length) {
        this.adicionarLinha();
      }
    },
    resumoProduto(row) {
      const p = row._produto;
      if (p) return `${p.cod || p.produto_cod} — ${p.desc || ""}`;
      if (row.produto_cod != null) return `Código ${row.produto_cod}`;
      return "";
    },
    onInputQuantidadeBr(evt, row, field) {
      const el = evt?.target;
      if (!el || !row || !field) return;
      const next = sanitizarQuantidadeBr(el.value);
      row[field] = next === "" ? null : next;
      const show = next === "" ? "" : next;
      if (el.value !== show) el.value = show;
    },
    onInputBuscaProduto(idx, raw) {
      const row = this.form.produtos[idx];
      if (!row) return;
      row._busca = raw;
      if (this.produtoBuscaTimers[idx]) clearTimeout(this.produtoBuscaTimers[idx]);
      const q = String(raw || "").trim();
      if (q.length < 2) {
        row._resultados = [];
        row._buscando = false;
        return;
      }
      row._buscando = true;
      this.produtoBuscaTimers[idx] = setTimeout(async () => {
        try {
          row._resultados = await buscarProdutosOs2(q);
        } catch {
          row._resultados = [];
        } finally {
          row._buscando = false;
        }
      }, 280);
    },
    fecharDropdownDelayed(idx) {
      setTimeout(() => {
        const row = this.form.produtos[idx];
        if (row) row._dropdown = false;
      }, 150);
    },
    selecionarProduto(idx, p) {
      const row = this.form.produtos[idx];
      if (!row || !p) return;
      row.produto_cod = p.produto_cod != null ? Number(p.produto_cod) : null;
      row._produto = p;
      row._busca = "";
      row._resultados = [];
      row._dropdown = false;
    },
    validarProdutos() {
      const incompletas = this.form.produtos.filter((row) => {
        const temBusca = String(row._busca || "").trim() !== "";
        return temBusca && !this.produtoLinhaRegistrada(row);
      });
      if (incompletas.length) {
        this.notificar("error", "Selecione o produto em todas as linhas preenchidas.");
        return false;
      }
      const registrados = this.form.produtos.filter((row) => this.produtoLinhaRegistrada(row));
      if (!registrados.length) {
        this.notificar("error", "Informe ao menos um produto para iniciar a OS.");
        return false;
      }
      const codigos = registrados.map((r) => Number(r.produto_cod));
      if (new Set(codigos).size !== codigos.length) {
        this.notificar("error", "Não repita o mesmo produto na lista.");
        return false;
      }
      const semQtDescrita = registrados.filter((row) => !this.qtDescritaValida(row));
      if (semQtDescrita.length) {
        this.notificar(
          "error",
          "Informe a quantidade descrita em todos os produtos da lista.",
        );
        return false;
      }
      return true;
    },
    validar() {
      if (this.form.pos_venda_id == null) {
        this.notificar("error", "Selecione o pós-venda de origem.");
        return false;
      }
      if (this.form.cliente_id == null) {
        this.notificar(
          "error",
          "O pós-venda selecionado deve ter um cliente vinculado.",
        );
        return false;
      }
      if (!String(this.form.dt_prevista_recebimento_material || "").trim()) {
        this.notificar("error", "Informe a data prevista de recebimento do material.");
        return false;
      }
      if (!String(this.form.nf_entrada || "").trim()) {
        this.notificar("error", "Informe a NF de remessa do cliente.");
        return false;
      }
      if (!this.validarProdutos()) return false;
      return true;
    },
    async salvar() {
      if (!this.validar()) return;
      this.salvando = true;
      try {
        const produtos = this.form.produtos
          .filter(
            (r) =>
              r.produto_cod != null && !Number.isNaN(Number(r.produto_cod)),
          )
          .map((r) => ({
            produto_cod: Number(r.produto_cod),
            qt_descrita: parseQuantidadeBrParaNumero(r.qt_descrita),
          }))
          .filter((r) => r.qt_descrita != null && r.qt_descrita > 0);
        const criada = await criarAssistenciaEmAnalise({
          pos_venda_id: this.form.pos_venda_id,
          cliente_id: this.form.cliente_id,
          dt_prevista_recebimento_material: this.form.dt_prevista_recebimento_material,
          observacoes: String(this.form.observacoes || "").trim() || null,
          nf_entrada: String(this.form.nf_entrada || "").trim(),
          produtos,
        });
        this.notificar("success", "Ordem criada — aguardando recebimento.");
        await this.$nextTick();
        setTimeout(() => {
          this.$emit("salvo", criada);
        }, 0);
      } catch (error) {
        console.error(error);
        const msg =
          error.response?.data?.message ||
          error.response?.data?.msg ||
          (error.response?.data?.errors &&
            JSON.stringify(error.response.data.errors)) ||
          error.message ||
          "Erro ao criar OS.";
        this.notificar("error", msg);
      } finally {
        this.salvando = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-conteudo {
  box-sizing: border-box;
  align-self: center;
  background: var(--cor-bg);
  border-radius: 8px;
  padding: 1.5rem;
  width: min(45rem, calc(100vw - 2rem));
  min-width: min(18rem, calc(100vw - 2rem));
  max-height: 90vh;
  overflow-y: auto;
}
.modal-conteudo h3 {
  margin: 0 0 1rem;
  font-size: 1.2rem;
}
.formulario {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}
.campos-datas {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: start;
}
@media (max-width: 520px) {
  .campos-datas {
    grid-template-columns: 1fr;
  }
}
.hint-campos-datas {
  margin: -0.35rem 0 0;
}
.campo label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.35rem;
}
.obrigatorio {
  color: var(--cor-erro, #c62828);
}
.hint-produtos {
  margin: 0 0 0.5rem;
  font-size: 0.9rem;
  color: var(--cor-fonte-fraca, #666);
}
.data-calculada {
  margin: 0;
  padding: 0.5rem 0.65rem;
  font-size: 1rem;
  font-weight: 500;
  border: 1px solid var(--cor-separador, #ccc);
  border-radius: 6px;
  background: var(--cor-cinza, rgba(0, 0, 0, 0.04));
  color: var(--cor-fonte, #333);
}
.hint-data-calculada {
  margin: 0.35rem 0 0;
  font-size: 0.85rem;
  color: var(--cor-fonte-fraca, #666);
}
.texto-na {
  color: var(--cor-fonte-fraca, #888);
  font-size: 0.9rem;
}
.campo input,
.campo select,
.campo textarea {
  width: 100%;
  max-width: 100%;
  padding: 0.5rem 0.65rem;
  border: 1px solid var(--cor-separador, #ccc);
  border-radius: 6px;
  font: inherit;
}
.tabela-produtos {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.9rem;
}
.tabela-produtos col.col-qtd {
  width: 6rem;
}
.tabela-produtos col.col-acoes {
  width: 6rem;
}
.tabela-produtos th.th-qtd,
.tabela-produtos td:nth-child(2) {
  text-align: center;
  white-space: nowrap;
}
.tabela-produtos th,
.tabela-produtos td {
  border: 1px solid var(--cor-separador, #ddd);
  padding: 0.35rem 0.5rem;
  vertical-align: middle;
}
.tabela-produtos td.td-produto,
.tabela-produtos th.th-produto {
  text-align: left !important;
}
.tabela-produtos td.td-acoes,
.tabela-produtos th.th-acoes {
  text-align: center !important;
}
.input-curto {
  width: 100%;
  max-width: 3.25rem;
  box-sizing: border-box;
  margin: 0 auto;
  display: block;
}
.busca-prod {
  position: relative;
}
.dropdown-prod {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
  background: var(--cor-bg);
  border: 1px solid var(--cor-separador, #ccc);
  max-height: 10rem;
  overflow-y: auto;
}
.dropdown-prod .opcao {
  padding: 0.35rem 0.5rem;
  cursor: pointer;
}
.dropdown-prod .opcao:hover {
  background: rgba(0, 0, 0, 0.06);
}
.prod-confirmado-texto {
  margin: 0;
  padding: 0.35rem 0;
  font-size: 0.9rem;
  line-height: 1.35;
  word-break: break-word;
  font-weight: 500;
}
.btn-remover {
  font-size: 0.85rem;
  background: transparent;
  border: none;
  color: var(--cor-erro, #c00);
  cursor: pointer;
}
.btn-add-linha {
  margin-top: 0.5rem;
}
.acoes {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
}
.acao-primaria {
  background: var(--cor-primaria);
  color: var(--cor-bg);
  border: none;
  padding: 0.5rem 1.1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
}
.acao-primaria:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
